/**
 * Diálogo CRUD para perfiles de mapeo Excel → SUENLACE (facturas emitidas).
 *
 * Flujo de configuración:
 *   1) El usuario sube una Excel de muestra del ERP del cliente.
 *   2) Elige hoja + fila de cabecera.
 *   3) Para cada campo lógico (Fecha, NIF, Base, IVA…) selecciona la columna
 *      del Excel que la contiene (por letra o por nombre de cabecera).
 *   4) Configura valores por defecto (cuenta cliente/venta, formato fecha).
 *   5) Guarda. El perfil queda disponible en el selector principal.
 */

import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Save, Trash2, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  ISSUED_FIELDS,
  type IssuedColumnMap,
  type IssuedDefaults,
  type IssuedFieldKey,
  type IssuedInvoiceMapping,
  createIssuedMapping,
  deleteIssuedMapping,
  loadIssuedMappings,
  updateIssuedMapping,
} from "@/lib/issuedInvoiceMapping";
import {
  columnLetterToIndex,
  indexToColumnLetter,
  previewExcel,
  type ParsedSheetPreview,
} from "@/lib/issuedInvoiceImport";
import { ISSUED_PRESETS, detectPresetFromHeaders } from "@/lib/issuedInvoicePresets";

interface Props {
  open: boolean;
  companyId: string;
  onClose: () => void;
  /** Se invoca cuando el usuario crea o modifica perfiles, para refrescar el selector. */
  onChange?: () => void;
}

const emptyMapping = (): IssuedInvoiceMapping => ({
  id: "",
  company_id: null,
  organization_id: "",
  name: "",
  erp_label: "",
  sheet_index: 0,
  header_row: 1,
  column_map: {},
  defaults: { dateFormat: "auto", decimalSeparator: "auto" },
  created_by: null,
  created_at: "",
  updated_at: "",
});


export default function IssuedMappingProfileDialog({ open, companyId, onClose, onChange }: Props) {
  const { toast } = useToast();
  const [profiles, setProfiles] = useState<IssuedInvoiceMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<IssuedInvoiceMapping | null>(null);
  const [preview, setPreview] = useState<ParsedSheetPreview | null>(null);
  const [sampleFile, setSampleFile] = useState<File | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    if (!companyId) return;
    setLoading(true);
    const list = await loadIssuedMappings(companyId);
    setProfiles(list);
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      refresh();
      setEditing(null);
      setPreview(null);
      setSampleFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId]);

  const handleSampleFile = async (file: File, sheetIdx?: number, headerRow?: number) => {
    if (!editing) return;
    setSampleFile(file);
    setPreviewLoading(true);
    try {
      const pv = await previewExcel(
        file,
        sheetIdx ?? editing.sheet_index,
        headerRow ?? editing.header_row,
      );
      setPreview(pv);
      // Auto-detección de plantilla si todavía no hay columnas mapeadas.
      if (editing && Object.keys(editing.column_map).length === 0) {
        const preset = detectPresetFromHeaders(pv.headers);
        if (preset) {
          setEditing((prev) => prev && ({
            ...prev,
            erp_label: prev.erp_label || preset.label,
            sheet_index: preset.sheet_index,
            header_row: preset.header_row,
            column_map: { ...preset.column_map },
            defaults: { ...prev.defaults, ...preset.defaults },
          }));
          toast({
            title: "Plantilla detectada",
            description: `Se ha aplicado «${preset.label}». Revísala y guárdala.`,
          });
        }
      }
    } catch (e) {
      toast({
        title: "No se pudo leer el Excel",
        description: e instanceof Error ? e.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const applyPreset = (presetId: string) => {
    const preset = ISSUED_PRESETS.find((p) => p.id === presetId);
    if (!preset || !editing) return;
    setEditing({
      ...editing,
      erp_label: editing.erp_label || preset.label,
      sheet_index: preset.sheet_index,
      header_row: preset.header_row,
      column_map: { ...preset.column_map },
      defaults: { ...editing.defaults, ...preset.defaults },
    });
    toast({ title: `Plantilla «${preset.label}» aplicada` });
  };


  const handleSave = async () => {
    if (!editing) return;
    if (!editing.name.trim()) {
      toast({ title: "Falta el nombre del perfil", variant: "destructive" });
      return;
    }
    // Validar campos obligatorios mapeados
    const missing = ISSUED_FIELDS.filter((f) => f.required && !editing.column_map[f.key]);
    if (missing.length > 0) {
      toast({
        title: "Faltan campos obligatorios",
        description: missing.map((m) => m.label).join(", "),
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    if (editing.id) {
      const { error } = await updateIssuedMapping(editing.id, {
        name: editing.name,
        erp_label: editing.erp_label,
        sheet_index: editing.sheet_index,
        header_row: editing.header_row,
        column_map: editing.column_map,
        defaults: editing.defaults,
      });
      if (error) {
        toast({ title: "Error guardando", description: error, variant: "destructive" });
      } else {
        toast({ title: "Perfil actualizado" });
        await refresh();
        setEditing(null);
        onChange?.();
      }
    } else {
      const { data, error } = await createIssuedMapping({
        name: editing.name,
        erp_label: editing.erp_label,
        sheet_index: editing.sheet_index,
        header_row: editing.header_row,
        column_map: editing.column_map,
        defaults: editing.defaults,
      });

      if (error || !data) {
        toast({ title: "Error creando", description: error, variant: "destructive" });
      } else {
        toast({ title: "Perfil creado" });
        await refresh();
        setEditing(null);
        onChange?.();
      }
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Eliminar este perfil de mapeo?")) return;
    const { error } = await deleteIssuedMapping(id);
    if (error) {
      toast({ title: "Error eliminando", description: error, variant: "destructive" });
      return;
    }
    toast({ title: "Perfil eliminado" });
    await refresh();
    onChange?.();
  };

  const columnOptions = useMemo(() => {
    // Aseguramos que las letras ya mapeadas aparezcan en el desplegable,
    // aunque todavía no se haya subido un Excel de muestra.
    const mappedMax = Object.values(editing?.column_map ?? {}).reduce((acc, letter) => {
      const idx = columnLetterToIndex(letter as string);
      return idx > acc ? idx : acc;
    }, 0);
    const max = Math.max(preview?.headers.length ?? 0, mappedMax, 42); // 42 = hasta columna AP
    return Array.from({ length: max }, (_, i) => {
      const letter = indexToColumnLetter(i + 1);
      const header = preview?.headers[i] || "";
      return { letter, header };
    });
  }, [preview, editing?.column_map]);

  const setMapField = (field: IssuedFieldKey, letter: string) => {
    if (!editing) return;
    const next: IssuedColumnMap = { ...editing.column_map };
    if (letter) next[field] = letter;
    else delete next[field];
    setEditing({ ...editing, column_map: next });
  };

  const setDefault = <K extends keyof IssuedDefaults>(key: K, value: IssuedDefaults[K]) => {
    if (!editing) return;
    setEditing({ ...editing, defaults: { ...editing.defaults, [key]: value } });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>Plantillas de importación — Facturas emitidas</DialogTitle>
          <DialogDescription>
            Las plantillas son comunes para toda la asesoría: define cada ERP una vez y reutilízala en cualquier cliente.
          </DialogDescription>

        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          {!editing ? (
            <div className="space-y-3">
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() => setEditing(emptyMapping())}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" /> Nuevo perfil
                </Button>
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : profiles.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Aún no hay plantillas. Crea la primera pulsando «Nuevo perfil».
                </p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {profiles.map((p) => (
                    <li key={p.id} className="flex items-center gap-3 px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{p.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {p.erp_label || "Sin ERP especificado"} · Hoja {p.sheet_index + 1} · Cabecera fila {p.header_row}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing({ ...p })}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(p.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Nombre del perfil</Label>
                  <Input
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="Ej: Sage 50 — Cliente X"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>ERP de origen</Label>
                  <Input
                    value={editing.erp_label}
                    onChange={(e) => setEditing({ ...editing, erp_label: e.target.value })}
                    placeholder="Ej: Sage 50, Holded, FactuSOL…"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Hoja (1 = primera)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={editing.sheet_index + 1}
                    onChange={(e) => {
                      const v = Math.max(1, parseInt(e.target.value || "1", 10)) - 1;
                      setEditing({ ...editing, sheet_index: v });
                      if (sampleFile) handleSampleFile(sampleFile, v, editing.header_row);
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Fila de cabeceras</Label>
                  <Input
                    type="number"
                    min={1}
                    value={editing.header_row}
                    onChange={(e) => {
                      const v = Math.max(1, parseInt(e.target.value || "1", 10));
                      setEditing({ ...editing, header_row: v });
                      if (sampleFile) handleSampleFile(sampleFile, editing.sheet_index, v);
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2 rounded-md border border-dashed p-3">
                <Label className="text-sm font-semibold">Excel de muestra (opcional)</Label>
                <p className="text-xs text-muted-foreground">
                  Sube una Excel real del ERP para ver las cabeceras detectadas y elegir cada columna por nombre.
                  El archivo no se guarda; solo se usa para configurar el mapeo.
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleSampleFile(f);
                    }}
                    className="text-xs"
                  />
                  {previewLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                </div>
                {preview && (
                  <p className="text-xs text-muted-foreground">
                    Detectadas {preview.headers.filter((h) => h).length} columnas con cabecera ·{" "}
                    {preview.rowCount} filas de datos en la hoja.
                  </p>
                )}
              </div>

              <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                <Label className="text-sm font-semibold">Plantilla de ERP (opcional)</Label>
                <p className="text-xs text-muted-foreground">
                  Aplica una de las plantillas precargadas para los ERPs ya conocidos.
                  Sobrescribirá el mapeo actual.
                </p>
                <Select onValueChange={applyPreset}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Aplicar plantilla…" />
                  </SelectTrigger>
                  <SelectContent>
                    {ISSUED_PRESETS.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-semibold">Mapeo de columnas</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {ISSUED_FIELDS.map((f) => (
                    <div key={f.key} className="space-y-1">
                      <Label className="text-xs">
                        {f.label}
                        {f.required && <span className="text-destructive ml-1">*</span>}
                      </Label>
                      <Select
                        value={editing.column_map[f.key] ?? "__none__"}
                        onValueChange={(v) => setMapField(f.key, v === "__none__" ? "" : v)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="— sin asignar —" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value="__none__">— sin asignar —</SelectItem>
                          {columnOptions.map((c) => (
                            <SelectItem key={c.letter} value={c.letter}>
                              {c.letter}
                              {c.header ? ` — ${c.header}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {f.hint && <p className="text-[10px] text-muted-foreground">{f.hint}</p>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-semibold">Valores por defecto</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Cuenta de cliente por defecto</Label>
                    <Input
                      value={editing.defaults.defaultCustomerAccount ?? ""}
                      onChange={(e) => setDefault("defaultCustomerAccount", e.target.value)}
                      placeholder="Ej: 43000000"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Cuenta de venta por defecto</Label>
                    <Input
                      value={editing.defaults.defaultSalesAccount ?? ""}
                      onChange={(e) => setDefault("defaultSalesAccount", e.target.value)}
                      placeholder="Ej: 70000000"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Formato de fecha</Label>
                    <Select
                      value={editing.defaults.dateFormat ?? "auto"}
                      onValueChange={(v) => setDefault("dateFormat", v as IssuedDefaults["dateFormat"])}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Detectar automáticamente</SelectItem>
                        <SelectItem value="dd/mm/yyyy">dd/mm/aaaa</SelectItem>
                        <SelectItem value="yyyy-mm-dd">aaaa-mm-dd</SelectItem>
                        <SelectItem value="mm/dd/yyyy">mm/dd/aaaa (EE.UU.)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Separador decimal</Label>
                    <Select
                      value={editing.defaults.decimalSeparator ?? "auto"}
                      onValueChange={(v) => setDefault("decimalSeparator", v as IssuedDefaults["decimalSeparator"])}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Detectar automáticamente</SelectItem>
                        <SelectItem value=",">Coma decimal (1.234,56)</SelectItem>
                        <SelectItem value=".">Punto decimal (1,234.56)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Cuentas de cobro por forma de pago</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const map = { ...(editing.defaults.paymentAccountMap ?? {}) };
                      map[""] = "";
                      setDefault("paymentAccountMap", map);
                    }}
                  >
                    <Plus className="h-3 w-3 mr-1" /> Añadir
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Si la columna "Forma de cobro" del Excel coincide con una entrada (mayúsculas, sin acentos),
                  se generará un asiento de cobro: Debe la cuenta de tesorería / Haber la cuenta del cliente.
                  Deja el mapa vacío para no generar ningún asiento de cobro (por ejemplo si es TRANSFERENCIA).
                </p>
                <div className="space-y-2">
                  {Object.entries(editing.defaults.paymentAccountMap ?? {}).map(([key, acc], idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                      <Input
                        placeholder="CONTADO / GIRO / TARJETA…"
                        value={key}
                        onChange={(e) => {
                          const newKey = e.target.value;
                          const entries = Object.entries(editing.defaults.paymentAccountMap ?? {});
                          entries[idx] = [newKey, acc];
                          const map: Record<string, string> = {};
                          for (const [k, v] of entries) map[k] = v;
                          setDefault("paymentAccountMap", map);
                        }}
                      />
                      <Input
                        placeholder="Cuenta tesorería (ej: 57000000000)"
                        value={acc}
                        onChange={(e) => {
                          const map = { ...(editing.defaults.paymentAccountMap ?? {}) };
                          map[key] = e.target.value;
                          setDefault("paymentAccountMap", map);
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const map = { ...(editing.defaults.paymentAccountMap ?? {}) };
                          delete map[key];
                          setDefault("paymentAccountMap", map);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {!Object.keys(editing.defaults.paymentAccountMap ?? {}).length && (
                    <p className="text-xs text-muted-foreground italic">
                      Sin cobros configurados — todas las facturas quedarán pendientes en la cuenta del cliente.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t pt-3">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
                Volver
              </Button>
              <Button onClick={handleSave} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Guardar perfil
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
